import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { company } from "../data/company";

const SLIDES = [
  {
    keys: ["about.p1a", "about.p1b"],
    highlight: ["about.p1a"],
    image: "about-sectors.png",
    altKey: "about.imageAlt1",
    imageRight: false,
    tone: "tone-sectors",
    watermark: "25+",
  },
  {
    keys: ["about.p2"],
    image: "about-partnership.png",
    altKey: "about.imageAlt2",
    imageRight: true,
    tone: "tone-partner",
  },
  {
    keys: ["about.p3"],
    image: "about-standards.png",
    altKey: "about.imageAlt3",
    imageRight: false,
    tone: "tone-standards",
  },
  {
    keys: ["about.p4a", "about.p4b"],
    highlight: ["about.p4b"],
    image: "about-supply.png",
    altKey: "about.imageAlt4",
    imageRight: true,
    tone: "tone-supply",
  },
];

const INTERVAL_MS = 5000;
const TRANSITION_MS = 700;

const Chapter = ({ slide }) => {
  const { t } = useTranslation();
  const highlighted = new Set(slide.highlight || []);

  return slide.keys.map((key) => (
    <p key={key} className="about-story-chapter">
      {highlighted.has(key) ? (
        <Trans
          i18nKey={key}
          values={{ name: company.shortName }}
          components={{ highlight: <span className="about-highlight" /> }}
        />
      ) : (
        t(key)
      )}
    </p>
  ));
};

const AboutSlide = ({ slide }) => {
  const { t } = useTranslation();

  return (
    <article className={`about-slide ${slide.tone}${slide.imageRight ? " is-image-right" : ""}`}>
      <div className="about-slide-layout">
      <figure className="about-story-visual">
        <img
          src={`${process.env.PUBLIC_URL}/${slide.image}`}
          alt={t(slide.altKey)}
          className="about-story-image"
          loading="lazy"
        />
        {slide.watermark && (
          <span className="about-story-watermark" aria-hidden="true">
            {slide.watermark}
          </span>
        )}
      </figure>
      <div className="about-story-copy">
        <Chapter slide={slide} />
      </div>
      </div>
    </article>
  );
};

const AboutSection = () => {
  const { t, i18n } = useTranslation();
  const [index, setIndex] = useState(0);
  const [instant, setInstant] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [cycle, setCycle] = useState(0);

  const track = [...SLIDES, SLIDES[0]];
  const active = index % SLIDES.length;

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onPreference = () => setReducedMotion(media.matches);
    onPreference();
    media.addEventListener("change", onPreference);
    return () => media.removeEventListener("change", onPreference);
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const section = document.getElementById("about");
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        section.classList.add("is-entered");
        observer.disconnect();
      },
      { threshold: 0.08 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [reducedMotion]);

  useEffect(() => {
    const section = document.getElementById("about");
    if (!section || reducedMotion) return undefined;

    let frame = 0;
    let lift = 0;

    const update = () => {
      frame = 0;
      const layoutTop = section.getBoundingClientRect().top - lift;
      const travel = window.innerHeight * 0.5;
      const progress = Math.min(1, Math.max(0, (window.innerHeight - layoutTop) / travel));
      const eased = 1 - (1 - progress) ** 3;
      lift = (1 - eased) * 42;
      section.style.transform = lift < 0.5 ? "" : `translateY(${lift}px)`;
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      section.style.transform = "";
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const timer = setInterval(() => {
      setIndex((current) => current + 1);
    }, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [reducedMotion, cycle]);

  useEffect(() => {
    if (index < SLIDES.length) return undefined;
    const timeout = setTimeout(() => {
      setInstant(true);
      setIndex(0);
    }, TRANSITION_MS);
    return () => clearTimeout(timeout);
  }, [index]);

  useEffect(() => {
    if (!instant) return undefined;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setInstant(false));
    });
    return () => cancelAnimationFrame(frame);
  }, [instant]);

  const goTo = (next) => {
    setInstant(false);
    setIndex(next);
    setCycle((current) => current + 1);
  };

  const header = (
    <div className="about-story-header">
      <h2 className="about-story-title">{t("about.title")}</h2>
      <p className="about-story-lead">{t("about.lead")}</p>
    </div>
  );

  const closing = (
    <div className="about-story-closing">
      <p className="about-story-signoff">{t("about.signoff")}</p>
      <p className="about-cta about-story-motto">{t("about.cta")}</p>
    </div>
  );

  if (reducedMotion) {
    return (
      <section id="about" className="about-story about-story-static">
        {header}
        {SLIDES.map((slide) => (
          <AboutSlide key={slide.image} slide={slide} />
        ))}
        {closing}
      </section>
    );
  }

  return (
    <section id="about" className="about-story">
      <div className="about-story-viewport">
        <div
          className={`about-story-track${instant ? " is-instant" : ""}`}
          style={{
            transform: `translateX(${(i18n.dir() === "rtl" ? index : -index) * 100}%)`,
          }}
        >
          {track.map((slide, trackIndex) => {
            const slideIndex = trackIndex % SLIDES.length;
            return (
              <div
                key={`${slide.image}-${trackIndex}`}
                className="about-story-frame"
                aria-hidden={trackIndex === SLIDES.length || slideIndex !== active}
              >
                <AboutSlide slide={slide} />
              </div>
            );
          })}
        </div>
        {header}
        <div className="about-story-footer">
          <div className="about-story-steps" aria-label={t("about.title")}>
            {SLIDES.map((slide, slideIndex) => (
              <button
                key={slide.image}
                type="button"
                className={slideIndex === active ? "is-active" : ""}
                aria-current={slideIndex === active ? "true" : undefined}
                aria-label={`${t("about.title")} ${slideIndex + 1}`}
                onClick={() => goTo(slideIndex)}
              />
            ))}
          </div>
          {closing}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
